'use client';

import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Box,
  Progress,
} from '@chakra-ui/react';
import type { ViewState } from 'react-map-gl';
import type {
  Broker,
  NestedAutocompleteAddress,
  NestedHome,
  SellerIntentType,
} from '@habity/types';
import { SellStepperContainer } from '~/components/onboarding/sell/SellStepperContainer';
import { MapView } from '~/components/onboarding/sell/MapView';
import { MAPBOX_CONFIG } from '~/constants/mapbox';

const { INITIAL_VIEW_STATE } = MAPBOX_CONFIG;

export interface SellStepperModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialAddress?: NestedAutocompleteAddress | null;
  initialBroker?: Broker | null;
  addressBroker?: Broker | null;
  intent: SellerIntentType;
  locationName?: string | null;
  leadName?: string;
  leadEmail?: string;
  leadPhone?: string;
  leadSourceId?: string;
  assignmentSourceId?: string;
  onComplete?: (home?: NestedHome) => void;
}

export function SellStepperModal({
  isOpen,
  onClose,
  initialAddress,
  initialBroker,
  addressBroker,
  intent,
  locationName,
  leadName,
  leadEmail,
  leadPhone,
  leadSourceId,
  assignmentSourceId,
  onComplete,
}: SellStepperModalProps) {
  const [step, setStep] = React.useState(0);
  const [total, setTotal] = React.useState(4);
  const [viewState, setViewState] = useState<Partial<ViewState>>({
    ...INITIAL_VIEW_STATE,
    padding: { top: 0, bottom: 0, left: 0, right: 0 },
  });
  const mapRef = useRef<any>(null);

  const handleComplete = (completedHome?: NestedHome) => {
    onComplete?.(completedHome);
    onClose();
  };

  // Callback to update progress from stepper
  const handleStepChange = React.useCallback(
    (currentStep: number, totalSteps: number) => {
      setStep(currentStep);
      setTotal(totalSteps);
    },
    []
  );

  // Global Escape key handler - needed because autofocus on inputs steals focus from modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: 'full', lg: 'md' }}
      motionPreset="slideInBottom"
      scrollBehavior="inside"
      isCentered
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent
        bg="white"
        borderRadius="0"
        m={{ base: 0, lg: 'auto' }}
        position="relative"
      >
        <ModalCloseButton zIndex={11} />

        {/* Progress indicator at top */}
        {total > 0 && (
          <Box
            position={{ base: 'fixed', lg: 'absolute' }}
            top={0}
            left={0}
            right={0}
            zIndex={10}
          >
            <Progress
              value={((step + 1) / total) * 100}
              size="sm"
              colorScheme="gray"
              borderRadius="0"
              sx={{ '& > div': { bg: 'black' } }}
            />
          </Box>
        )}

        <SellStepperContainer
          initialAddress={initialAddress}
          initialBroker={initialBroker}
          addressBroker={addressBroker}
          intent={intent}
          locationName={locationName}
          leadName={leadName}
          leadEmail={leadEmail}
          leadPhone={leadPhone}
          leadSourceId={leadSourceId}
          assignmentSourceId={assignmentSourceId}
          onComplete={handleComplete}
          onStepChange={handleStepChange}
        >
          {({ stepperElement, selectedAddress, broker }) => {
            // Update map view when address changes
            React.useEffect(() => {
              if (selectedAddress?.latitude && selectedAddress?.longitude) {
                setViewState({
                  longitude: selectedAddress.longitude,
                  latitude: selectedAddress.latitude,
                  zoom: 16,
                  padding: { top: 0, bottom: 0, left: 0, right: 0 },
                });
              }
            }, [selectedAddress?.id]);

            // Hide map on verification step (last step before confirmation) to give more vertical space for the login form
            // In short flow: steps are 0-3 (street, address, category, verification), so step 3 is verification
            const isVerificationStep = step === total - 1;

            return (
              <>
                {/* Map Section - hidden on verification step */}
                {selectedAddress && !isVerificationStep && (
                  <MapView
                    selectedAddress={selectedAddress}
                    viewState={viewState}
                    onMove={(e) => setViewState(e.viewState)}
                    mapRef={mapRef}
                    height={{ base: '200px', lg: '250px' }}
                    interactive={false}
                  />
                )}

                {/* Stepper Content */}
                <ModalBody p={6} overflow="auto">
                  <Box mt={total > 0 ? 4 : 0}>{stepperElement}</Box>
                </ModalBody>
              </>
            );
          }}
        </SellStepperContainer>
      </ModalContent>
    </Modal>
  );
}
